import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Alert, Button } from '@mui/material';

import ActivityContainer from 'components/Common/ActivityContainerTable';
import ActivityHeader from 'components/Common/activity/ActivityHeader';
import { AppDispatch, RootState } from 'redux/store';
import { clearHistory, fetchHistory } from 'redux/slices/history/HistorySlice';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import { dateFormat, formatAmount, timeFormat } from 'utils/helpers';
import { ParishionerActivityHeaderProps } from 'types/community';
import type { MemberDonation, MemberDonationStatus } from 'types/members';

/**
 * Status labels. 'Complete' and 'Pending' are StyledTable's own green/orange values
 * (`successValue` and its built-in 'Pending' colour), so those two render coloured for free.
 */
const STATUS_LABEL: Record<MemberDonationStatus, string> = {
    SUCCEEDED: 'Complete',
    PROCESSING: 'Pending',
    PARTIALLY_REFUNDED: 'Partially refunded',
    REFUNDED: 'Refunded',
    DISPUTED: 'Reversed (disputed)',
};

const cents = (value: number) => formatAmount(value, 'cents');

const columns = [
    { id: 'date', label: 'Date', render: ({ data }: { data: MemberDonation }) => dateFormat(data.createdAt) },
    { id: 'time', label: 'Time', render: ({ data }: { data: MemberDonation }) => timeFormat(data.createdAt) },
    { id: 'basketName', label: 'Basket' },
    { id: 'amountCents', label: 'Gift', render: ({ value }: { value: number }) => cents(value) },
    { id: 'refundedCents', label: 'Refunded', render: ({ value }: { value: number }) => (value > 0 ? cents(value) : '—') },
    { id: 'netCents', label: 'Net', render: ({ value }: { value: number }) => cents(value) },
    { id: 'status', label: 'Status', render: ({ value }: { value: MemberDonationStatus }) => STATUS_LABEL[value] ?? value },
];

const activityHeaderFields = ({ lastGift, totalGiven }: ParishionerActivityHeaderProps) => [
    { value: 'Last Gift', endAdornmentValue: lastGift },
    { value: 'Total Given', endAdornmentValue: totalGiven },
];

/** DateFilter output: presets set dayFrom/dayTo (local-time ISO with offset); 'All' omits both. */
type ActivityFilter = { dayFrom?: string; dayTo?: string; [key: string]: unknown };

const ParishionerActivityContainer = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { uuid } = useParams();
    const parishId = useSelector(selectSelectedParishId);
    const { records, loading, totalCount, summary, error } = useSelector((state: RootState) => state.history);
    const { pageNumber, pageSize } = useSelector((state: RootState) => state.app.table);
    // Starts on "All" (no range) — matches `dateFilterDefault="all"` below.
    const [filter, setFilter] = useState<ActivityFilter>({});
    const from = filter.dayFrom;
    const to = filter.dayTo;

    const load = useCallback(() => {
        if (!parishId || !uuid) return;
        dispatch(fetchHistory({ parishId, userId: uuid, page: pageNumber + 1, pageSize, from, to }));
    }, [dispatch, parishId, uuid, pageNumber, pageSize, from, to]);

    useEffect(() => {
        load();
    }, [load]);

    // Never flash one member's history on another member's screen.
    useEffect(() => {
        return () => {
            dispatch(clearHistory());
        };
    }, [dispatch, uuid]);

    const isRange = Boolean(from || to);
    const headerProps: ParishionerActivityHeaderProps = {
        lastGift: summary?.lastDonationAt ? dateFormat(summary.lastDonationAt) : '—',
        totalGiven: summary ? cents(summary.netCents) : '—',
    };

    // On error the inline Alert (with Retry) is the only message — the table shows no empty state.
    const emptyMessage = error ? null : isRange ? 'No gifts in this period.' : 'No gifts from this parishioner yet.';

    return (
        <>
            {error && (
                <Alert
                    severity="error"
                    sx={{ mx: 2, mt: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}
            <ActivityContainer
                navigationHandler={() => undefined}
                disableRowNavigation
                records={records}
                columns={columns}
                filter={filter}
                setFilter={setFilter}
                dateFilterDefault="all"
                totalCount={totalCount}
                serverSidePagination
                loading={loading}
                showLoading
                emptyMessage={emptyMessage}
                activityHeader={<ActivityHeader fields={activityHeaderFields(headerProps)} />}
            />
        </>
    );
};

export default ParishionerActivityContainer;
